import React from "react";
import "../GenericPrintTable.css";

export interface PrintColumn<T> {
  header: string;
  accessor: keyof T | string;
  render?: (context: { row: { original: T }; getValue: () => any }) => React.ReactNode;
}

interface GenericPrintModalProps<T> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: T[];
  columns: PrintColumn<T>[];
}

export function GenericPrintModal<T>({
  isOpen,
  onClose,
  title,
  data,
  columns,
}: GenericPrintModalProps<T>) {
  if (!isOpen) return null;

  const currentDate = new Date().toLocaleDateString("el-GR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 print:p-0 print:bg-white print:static">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col print:shadow-none print:max-w-none print:max-h-none print:overflow-visible">

        {/* Controls: Κρύβονται αυτόματα κατά την εκτύπωση λόγω της κλάσης no-print */}
        <div className="flex justify-between items-center p-4 border-b no-print">
          <h3 className="text-lg font-bold text-slate-800">Προεπισκόπηση Εκτύπωσης</h3>
          <div className="flex items-center gap-2">
<button
  onClick={() => window.print()}
  className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-darkprimary transition-colors shadow-sm"
>
  Εκτύπωση
</button>
            <button
              onClick={onClose}
              className="px-3 py-2 bg-slate-200 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-300"
            >
              Κλείσιμο
            </button>
          </div>
        </div>

        {/* Περιεχόμενο Modal / Εκτύπωσης */}
        <div className="p-6 overflow-y-auto flex-1 print:p-0 print:overflow-visible">

          {/* Ενιαίο Header που εμφανίζεται και στην οθόνη και στην εκτύπωση */}
          <div className="flex justify-between items-center border-b pb-2 mb-4">
            <h1 className="text-xl font-bold text-slate-900">{title}</h1>
            <span className="text-sm text-slate-500">Ημερομηνία: {currentDate}</span>
          </div>

          <table className="print-table w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-100 print:bg-slate-200">
                {columns.map((col, idx) => (
                  <th key={idx} className="px-3 py-2 font-semibold text-slate-700">
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((row, rowIdx) => (
                  <tr key={rowIdx} className="border-b hover:bg-slate-50">
                    {columns.map((col, colIdx) => {
                      const cellContext = {
                        row: { original: row },
                        getValue: () => (row as any)[col.accessor],
                      };

                      return (
                        <td key={colIdx} className="px-3 py-2 text-slate-600">
                          {col.render
                            ? col.render(cellContext as any)
                            : String((row as any)[col.accessor] ?? "-")}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-4 text-center text-slate-500">
                    Δεν υπάρχουν διαθέσιμα δεδομένα.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}